<template>
    <Modal :show="show" @close="closeModal">
        <div class="flex flex-col w-full">
            <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 mb-4">
                    Edit Page: <Translate :value="pageData?.title" />
                </h3>
                
                <!-- Basic Page Info (Read-Only) -->
                <div class="mb-6 p-3 bg-gray-50 rounded">
                    <h4 class="font-medium text-gray-900 mb-2">Page Information:</h4>
                    <div class="grid grid-cols-2 gap-4 text-sm">
                        <p class="text-gray-700"><strong>ID:</strong> {{ pageData?.id }}</p>
                        <p class="text-gray-700"><strong>Title:</strong> <Translate :value="pageData?.title" /></p>
                        <p class="text-gray-700"><strong>Route:</strong> {{ pageData?.route }}</p>
                        <p class="text-gray-700"><strong>View:</strong> {{ pageData?.View }}</p>
                        <p class="text-gray-700"><strong>Parent ID:</strong> {{ pageData?.parent || 'Root Page' }}</p>
                    </div>
                </div>
                
                <!-- Editable Fields -->
                <div class="space-y-6">
                    
                    <!-- Access Roles -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            <Translate value="$PageEditor$:access-roles" />
                            <RequiredStar />
                        </label>
                        
                        <Select 
                            v-model="formData.accessArray"
                            :options="availableRoleOptions"
                            valueKey="value"
                            textKey="label"
                            placeholder="Select roles..."
                            class="w-full"
                            :multiple="true"
                            sortBy="value"
                            sortDirection="asc"
                        />
                        
                        <!-- Access Preview -->
                        <div v-if="formData.accessArray.length > 0" class="mt-2 p-2 bg-gray-50 rounded text-sm">
                            <p class="text-gray-600">
                                <strong>Selected Roles:</strong> {{ selectedRoleNames.join(', ') || 'None' }}
                            </p>
                            <p class="text-gray-600">
                                <strong>Access String:</strong> {{ formattedAccessString }}
                            </p>
                        </div>
                    </div>
                    
                    <!-- Icon -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            Icon
                            <RequiredStar />
                        </label>
                        <input
                            v-model="formData.icon"
                            type="text"
                            name="icon"
                            placeholder="Enter icon name (e.g., Tools, Home, User)"
                            required
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p class="text-gray-500 text-xs mt-1">
                            Icon name used for display. Common icons: Tools, Home, User, Settings, Mail, etc.
                        </p>
                    </div>
                    
                    <!-- Display Type -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            <Translate value="$PageEditor$:display-type" />
                            <RequiredStar />
                        </label>
                        
                        <Select 
                            v-model="formData.displayType"
                            :options="displayTypeOptions"
                            valueKey="value"
                            textKey="label"
                            placeholder="Select display type..."
                            class="w-full"
                            :multiple="false"
                        />
                        
                        <!-- Display Type Preview -->
                        <div v-if="formData.displayType" class="mt-2 p-2 bg-gray-50 rounded text-sm">
                            <p class="text-gray-600">
                                <strong>Selected Type:</strong> {{ displayTypeOptions.find(dt => dt.value === formData.displayType)?.label || 'Unknown' }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <Button 
                    @click="closeModal"
                    Color="gray-500"
                    TextColor="white">
                    Cancel
                </Button>
                <Button 
                    @click="saveChanges"
                    :disabled="!hasChanges"
                    :Color="hasChanges ? 'main' : 'gray-300'"
                    :TextColor="hasChanges ? 'white' : 'gray-500'">
                    Save Changes
                </Button>
            </div>
        </div>
    </Modal>
</template>

<script>
import Modal from '@/components/Blocks/Modal.vue'
import Button from '@/components/Blocks/Button.vue'
import RequiredStar from '@/components/Form/RequiredStar.vue'
import Select from '@/components/Blocks/Select.vue'
import Translate from '@/components/Utils/Translate.vue'
import FetchData from '@/utils/FetchData';

export default {
    name: 'EditPageModal',
    components: {
        Modal,
        Button,
        RequiredStar,
        Select,
        Translate
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        pageData: {
            type: Object,
            default: null
        },
        roles: {
            type: Array,
            default: () => []
        },
        PageDisplayTypes: {
            type: Array,
            default: () => []
        }
    },
    emits: ['close', 'page-updated'],
    data() {
        return {
            formData: {
                accessArray: [],
                icon: '',
                displayType: 1
            },
            originalFormData: {
                accessArray: [],
                icon: '',
                displayType: 1
            }
        }
    },
    computed: {
        availableRoleOptions() {
            return this.roles.map(role => ({
                value: role.id,
                label: `${role.id} - ${this.$Translate(role.name)}`,
                isProtected: role.id === 1, // Admin roles cannot be unchecked once selected
                selected: this.formData.accessArray.includes(role.id)
            }));
        },
        
        displayTypeOptions() {
            return this.PageDisplayTypes.map(displayType => ({
                value: displayType.id,
                label: this.$Translate(displayType.name),
                selected: this.formData.displayType === displayType.id
            }));
        },
        
        selectedRoleNames() {
            return this.formData.accessArray.map(roleId => {
                const role = this.roles.find(r => r.id === parseInt(roleId));
                return role ? this.$Translate(role.name) : "Unknown";
            });
        },
        
        formattedAccessString() {
            // Convert array [1, 2, 3] to new format "$1$2$3$"
            if (this.formData.accessArray.length === 0) return '';
            return '$' + this.formData.accessArray.join('$') + '$';
        },
        
        hasChanges() {
            // Compare current form data with original data
            return (
                JSON.stringify([...this.formData.accessArray].sort()) !== JSON.stringify([...this.originalFormData.accessArray].sort()) ||
                this.formData.icon !== this.originalFormData.icon ||
                this.formData.displayType !== this.originalFormData.displayType
            );
        },
        
        changedFields() {
            const changes = {};
            
            if (JSON.stringify([...this.formData.accessArray].sort()) !== JSON.stringify([...this.originalFormData.accessArray].sort())) {
                changes.access = {
                    original: this.originalFormData.accessArray,
                    new: this.formData.accessArray
                };
            }
            
            if (this.formData.icon !== this.originalFormData.icon) {
                changes.icon = {
                    original: this.originalFormData.icon,
                    new: this.formData.icon
                };
            }
            
            if (this.formData.displayType !== this.originalFormData.displayType) {
                changes.display_type = {
                    original: this.originalFormData.displayType,
                    new: this.formData.displayType
                };
            }
            
            return changes;
        }
    },
    watch: {
        show(newValue) {
            if (newValue && this.pageData) {
                this.initializeForm();
            }
        },
        
        pageData(newValue) {
            if (newValue && this.show) {
                this.initializeForm();
            }
        },
    },
    methods: {
        closeModal() {
            this.resetForm();
            this.$emit('close');
        },
        
        initializeForm() {
            if (!this.pageData) return;
            
            // Parse access string to array for the form
            let accessArray = [];
            if (this.pageData.access && this.pageData.access !== 'all') {
                if (this.pageData.access.toString().startsWith('$')) {
                    // New format: "$1$2$3$" -> [1, 2, 3]
                    accessArray = this.pageData.access.toString()
                        .split('$')
                        .filter(role => role !== '')
                        .map(role => parseInt(role))
                        .filter(role => !isNaN(role));
                } else {
                    // Old format: "123" -> [1, 2, 3]
                    accessArray = this.pageData.access.toString().split('').map(role => parseInt(role));
                }
            }
            
            // Populate form data
            this.formData = {
                accessArray: [...accessArray], // Create a copy
                icon: this.pageData.icon || '',
                displayType: this.pageData.display_type || 1
            };
            
            // Store original data for comparison
            this.originalFormData = {
                accessArray: [...accessArray], // Create a copy
                icon: this.pageData.icon || '',
                displayType: this.pageData.display_type || 1
            };
        },
        
        resetForm() {
            this.formData = {
                accessArray: [],
                icon: '',
                displayType: 1
            };
            this.originalFormData = {
                accessArray: [],
                icon: '',
                displayType: 1
            };
        },
        async saveChanges() {
            // Create an object with only the changed fields
            const changedData = {};
            const route = "PostPages/" + this.pageData.id;
            
            // Check each field and include only if changed
            if (JSON.stringify([...this.formData.accessArray].sort()) !== JSON.stringify([...this.originalFormData.accessArray].sort())) {
                changedData.access = this.formattedAccessString;
            }
            
            if (this.formData.icon !== this.originalFormData.icon) {
                changedData.icon = this.formData.icon;
            }
            
            if (this.formData.displayType !== this.originalFormData.displayType) {
                changedData.display_type = this.formData.displayType;
            }
            try{
                const response = await FetchData(changedData, route, "PUT");
                if(response.id){
                    this.$toast.add({
                        severity: "success",
                        summary: "Success",
                        detail: "Changes saved successfully.",
                        life: 3000,
                    });
                } else {
                    this.$toast.add({
                        severity: "error",
                        summary: "Error",
                        detail: "Failed to save changes. Please try again.",
                        life: 5000,
                    });
                }
            }catch(error){
                console.error("Error saving page changes:", error);
                if(this.$toast){
                    this.$toast.add({
                        severity: "error",
                        summary: "Error",
                        detail: "Failed to save changes. Please try again.",
                        life: 5000,
                    });
                }
                return; // Exit if there's an error
            }
            // Emit the changes
            this.$emit('page-updated');            
            this.closeModal();
        }
    }
};
</script>